// §6.1 — the remaining macOS chrome: notifications, the side panel, and the two screens.
//
// §6.3 puts these notifications "top-right banner → side panel", which is the one place this skin
// differs from the other two in *where* rather than in what: the banner drops from under the menu
// bar at the right, and what has been said lives on in §13's Center — which is the side panel, one
// component every skin mounts (D-140), placed against the right edge by this skin's own sheet.
//
// `LoginScreen` gets its content at M12.9.
import { useEffect } from 'react';
import { COPY } from '@/copy';
import { beginSignIn } from '@/kernel/server';
import { useKernel, useNotifications, useStoreValue } from '@/kernel/react';

/**
 * §6.3 — top-right, under the menu bar.
 *
 * Two sources, one place: §13's notifications, whose 6 s life the kernel owns, and `shellui`'s
 * single shell-authored toast (§6.4's stolen-chord message, a skipped session), which belongs to
 * no app and never reaches the Center.
 */
export function NotificationHost() {
  const kernel = useKernel();
  const toast = useStoreValue(kernel.shellui.store, (s) => s.toast);
  const { toasts } = useNotifications();

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => kernel.shellui.dismissToast(), 6_000);
    return () => clearTimeout(timer);
  }, [toast, kernel]);

  if (!toast && toasts.length === 0) return null;
  return (
    <div className="macos-banners" data-testid="notification-host">
      {toasts.map((item) => (
        <div
          key={item.id}
          className="macos-banner"
          role="status"
          data-testid={`notification-toast-${item.id}`}
          data-app-id={item.appId}
        >
          <span
            className="macos-banner-icon"
            aria-hidden
            dangerouslySetInnerHTML={{ __html: kernel.appIconSvg(item.appId, 28) }}
          />
          {/* §13 — clicking the body focuses the window that sent it. */}
          <button
            type="button"
            className="macos-banner-body"
            data-testid={`notification-toast-open-${item.id}`}
            onClick={() => kernel.notify.activate(item.id)}
          >
            <span className="macos-banner-title">{item.title}</span>
            {item.body !== undefined && item.body.length > 0 && (
              <span className="macos-banner-text">{item.body}</span>
            )}
          </button>
          {item.actions.map((action) => (
            <button
              key={action}
              type="button"
              className="macos-banner-action"
              data-testid={`notification-toast-action-${item.id}-${action}`}
              onClick={() => kernel.notify.act(item.id, action)}
            >
              {action}
            </button>
          ))}
          <button
            type="button"
            className="macos-banner-dismiss"
            aria-label={COPY.chrome.dismiss}
            onClick={() => kernel.notify.dismissToast(item.id)}
          >
            ×
          </button>
        </div>
      ))}
      {toast && (
        <div className="macos-banner" role="status" data-testid="shell-toast">
          <span data-testid="shell-toast-message">{toast.message}</span>
          <button
            type="button"
            className="macos-banner-dismiss"
            aria-label={COPY.chrome.dismiss}
            onClick={() => kernel.shellui.dismissToast()}
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * §6.3 — the side panel. §13's Center *is* the side panel on this skin, mounted by the shell and
 * placed against the right edge by `shell.css`, so this slot has nothing left to draw.
 *
 * It is not `null` by omission: the flyout on the Windows skin exists because its tray is a
 * separate surface from its Center. Here they are the same surface, which is what §6.3's arrow —
 * "banner → side panel" — describes.
 */
export function TrayFlyout() {
  return null;
}

/** §8.3 — the whole page when an admin has turned anonymous access off (M12.9). */
export function LoginScreen() {
  return (
    <div className="macos-login" data-testid="login-screen">
      <div className="macos-login-panel">
        <h1 className="macos-login-title">{COPY.loginTitle}</h1>
        <p className="macos-login-body">{COPY.loginBody}</p>
        <button
          type="button"
          className="macos-login-action"
          data-testid="login-signin"
          onClick={() => beginSignIn(() => undefined)}
        >
          {COPY.loginAction}
        </button>
      </div>
    </div>
  );
}

/** §6.4 — the lock screen: a full-bleed blur with the clock on it. */
export function LockScreen() {
  const kernel = useKernel();
  const locked = useStoreValue(kernel.shellui.store, (s) => s.overlay === 'lock');
  if (!locked) return null;
  return (
    <div
      className="macos-lock"
      data-testid="lock-screen"
      onClick={() => kernel.shellui.closeOverlay()}
    >
      <span className="macos-lock-clock" data-testid="lock-clock">
        {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
      </span>
    </div>
  );
}
